import type { BannerItem } from "$root/components/banner-stack/types";
import { formatCurrencyAmount } from "$root/utils/currency";
import { truncateToPrecision } from "$root/utils/monetary-math";
import { ActionText } from "@mdotm/shadcn/legacy/components";
import { spawnQuantityAumDialog } from "./AumDialog";
import type { QuantityEditorBuilderProps } from "./builder";

/**
 * Quantity-mode (MDOT-24700) guard banner: shown while the composition overspends the fixed
 * AUM budget (derived cash market value < 0). Submitting is blocked until the user either
 * raises the AUM — via the inline quick action, which opens the AUM dialog and commits
 * through the builder's `setAum` — or sells instruments (lowers quantities / deletes rows).
 */
export function negativeCashBanner({
	instrumentBuilder,
}: {
	instrumentBuilder: QuantityEditorBuilderProps;
}): BannerItem | null {
	const cashMarketValue = instrumentBuilder.getCashMarketValue();
	if (cashMarketValue == null || cashMarketValue >= 0) {
		return null;
	}
	const currency = instrumentBuilder.getCurrency();

	return {
		id: "negative-cash",
		severity: "warning",
		title: "Composition exceeds the liquidity",
		description: (
			<span data-qualifier="CompositionEditor/NegativeCashBanner">
				Cash is negative by{" "}
				<strong>{formatCurrencyAmount(truncateToPrecision(Math.abs(cashMarketValue), 2), currency)}</strong>. To submit,{" "}
				<ActionText
					data-qualifier="CompositionEditor/NegativeCashBanner/UpdateAum"
					onClick={() =>
						spawnQuantityAumDialog({
							currentAum: instrumentBuilder.getAum(),
							missingCash: Math.abs(cashMarketValue),
							currency,
							onConfirm: (newAum) => instrumentBuilder.setAum(newAum),
						})
					}
				>
					raise the portfolio AUM
				</ActionText>{" "}
				or sell instruments.
			</span>
		),
	};
}
